import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useStore } from '../context/StoreContext';
import { useToast } from '../hooks/useToast';
import { useScrollReveal } from '../hooks/useParallax';
import ToastContainer from '../components/Toast';
import api from '../utils/api';
import { hasProductBadge, normalizeProduct, normalizeCategory, buildCategoriesFromProducts } from '../utils/productUtils';
import { getImageUrl } from '../utils/imageUtils';


/* ── Parallax banner ─────────────────────── */
function ParallaxSection({ children, bgUrl, overlay = 'rgba(15,26,48,0.72)' }) {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const pct = (rect.top / window.innerHeight);
      el.style.transform = `translateY(${pct * 60}px)`; // Dynamic positioning kaaga matnum inline-il vaika-pattu-lladhu
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <section className="parallax-section">
      <div ref={bgRef} className="parallax-bg" style={{ backgroundImage: `url(${bgUrl})` }} />
      <div className="parallax-overlay" style={{ background: overlay }} />
      <div className="parallax-content">
        <div className="container">{children}</div>
      </div>
    </section>
  );
}

const API_ASSET_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
  .replace(/\/api\/?$/, '');

const getCategoryImageUrl = (image) => {
  if (!image) return getImageUrl('/images/category-placeholder.jpg');
  if (image.startsWith('/images/')) return getImageUrl(image);
  if (/^https?:\/\//i.test(image)) return image;
  return `${API_ASSET_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
};

export default function HomePage() {
  const { addToCart } = useStore();
  const { toasts, showToast } = useToast();
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [trending, setTrending] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [homeConfig, setHomeConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [freeShippingAbove, setFreeShippingAbove] = useState(999);
  useScrollReveal([productsLoading]);

  // Helper function to check if a section is enabled
  const isSectionEnabled = (sectionId) => {
    if (!homeConfig || !homeConfig.sections) return true; // Default to true if no config
    const section = homeConfig.sections.find(s => s.id === sectionId);
    return section ? section.enabled : true;
  };

  // Fetch home config
  useEffect(() => {
    const loadHomeConfig = async () => {
      try {
        const response = await api.get('/home-config');
        const config = response.data.data;
        setHomeConfig(config);
      } catch (error) {
        console.error('Error loading home config:', error);
        setHomeConfig(null); // Default to null if API fails
      } finally {
        setConfigLoading(false);
      }
    };

    loadHomeConfig();
  }, []);

  useEffect(() => {
    const loadShippingSettings = async () => {
      try {
        const response = await api.get('/store-settings');
        const threshold = response.data?.data?.shippingAlerts?.freeShippingAbove;
        if (typeof threshold === 'number') {
          setFreeShippingAbove(threshold);
        }
      } catch (error) {
        console.error('Error loading shipping settings:', error);
      }
    };

    loadShippingSettings();
  }, []);

  /* Force video autoplay with strict handling */
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    
    vid.muted = true;
    vid.defaultMuted = true;
    
    const playPromise = vid.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.warn("Autoplay block elements encountered, retrying...", error);
        vid.muted = true;
        vid.play().catch(() => {
          console.error("Video completely failed to play.");
        });
      });
    }
  }, [videoFailed]);

  useEffect(() => {
    let ignore = false;

    const loadProducts = async () => {
      setProductsLoading(true);
      setProductsError(null);

      try {
        const response = await api.get('/products');

        if (!ignore) {
          const all = (response.data.products || []).map(normalizeProduct);
          const trendingList = all.filter((p) => p.trending).slice(0, 8);
          const featuredList = all.filter((p) => p.featuredOnHome).slice(0, 8);
          setTrending(trendingList);
          setFeatured(featuredList.length ? featuredList : all.slice(0, 8));
          setNewArrivals(all.filter((p) => hasProductBadge(p, 'New')).slice(0, 4));

          try {
            const catResponse = await api.get('/products/categories', {
              headers: { 'Cache-Control': 'no-cache' },
            });
            const loadedCats = (catResponse.data.categories || []).map(normalizeCategory);
            setCategories(loadedCats.length ? loadedCats : buildCategoriesFromProducts(all));
          } catch {
            setCategories(buildCategoriesFromProducts(all));
          }
        }
      } catch (error) {
        console.error(error);
        if (!ignore) {
          setProductsError(
            error.response?.data?.message || error.message || 'Failed to load products'
          );
        }
      } finally {
        if (!ignore) {
          setProductsLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      ignore = true;
    };
  }, []);

  const handleAddToCart = (product) => {
    addToCart({ ...product, variant: `${product.colors[0]} / ${product.sizes[0]}`, qty: 1 });
    showToast(`${product.name} added to cart`, 'fa-bag-shopping');
  };

  return (
    <>
      {/* ══ HERO VIDEO ══════════════════════════════════════════ */}
      <section className="hero-wrap">
        {!videoFailed ? (
          <video
            ref={videoRef}
            className="hero-video hero-video-style"
            poster=""
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onError={(e) => {
              console.error("Video loading error details:", e);
            }}
          >
            <source
              src={`${process.env.PUBLIC_URL}/images/pandian-store-file.mov`}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            className="hero-img"
            src="/images/jewellery-img1.jpg"
            alt="Pandian Store Hero Fallback"
          />
        )}
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">
            Crafted with<br /><em>Tamil Heritage</em>
          </h1>
          <p className="hero-subtitle">
            Exquisite jewellery, timeless fashion &amp; curated home décor —<br />rooted in South Indian tradition.
          </p>
          <div className="hero-actions">
            <Link to="/shop" className="btn-gold">
              <i className="fas fa-bag-shopping" /> Shop Now
            </Link>
            <Link to="/catalogue" className="btn-outline-white">
              <i className="fas fa-book-open" /> View Catalogue
            </Link>
          </div>
        </div>
      </section>

      {/* ══ CATEGORIES ══════════════════════════════════════════ */}
      {isSectionEnabled('cats') && (
      <section className="section-pad-sm section-white-bg">
        <div className="container">
          <div className="section-header reveal category-header-margin">
            <p className="section-eyebrow">Shop By Category</p>
            <h2 className="section-title">Explore Our <em>Collections</em></h2>
          </div>
          <div className="row g-3">
            {categories.map((cat, i) => (
              <div key={cat.id} className="col-6 col-md-3 reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                <Link to={`/shop?category=${cat.slug}`} className="cat-card">
                  <img src={getCategoryImageUrl(cat.image)} alt={cat.name} />
                  <div className="cat-card-body">
                    <p className="cat-card-name">
                      <i className={`fas ${cat.icon} cat-card-icon`} />
                      {cat.name}
                    </p>
                    <p className="cat-card-desc">{cat.description}</p>
                    <div className="cat-card-arrow">
                      <span className="cat-card-count">{cat.count} products</span>
                      <i className="fas fa-arrow-right cat-arrow-icon" />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ══ PARALLAX BANNER 1 ═══════════════════════════════════ */}
      <ParallaxSection
        bgUrl={`${process.env.PUBLIC_URL}/images/countbg-img1.jpg`}
      >
        <div className="row align-items-center gy-4">
          <div className="col-md-7">
            <p className="parallax-eyebrow">
              Handcrafted · Authentic · Timeless
            </p>
            <h2 className="parallax-title">
              Where Tradition Meets<br /><em className="gold-pale-text">Modern Elegance</em>
            </h2>
            <p className="parallax-desc">
              Every piece tells a story — from the hands of skilled Tamil artisans to your doorstep. Each product is certified authentic and crafted with generations of expertise.
            </p>
            <Link to="/shop" className="btn-gold">Explore Collection</Link>
          </div>
          <div className="col-md-5">
            <div className="stats-grid">
              {[
                { num: '2,400+', label: 'Happy Customers' },
                { num: '165+',   label: 'Products' },
                { num: '4.8★',   label: 'Avg Rating' },
                { num: '100%',   label: 'Authentic' },
              ].map(s => (
                <div key={s.label} className="stat-box">
                  <div className="stat-number">{s.num}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ParallaxSection>

      {/* ══ TRENDING ════════════════════════════════════════════ */}
      {isSectionEnabled('trending') && (
      <section className="section-pad section-cream-bg">
        <div className="container">
          <div className="section-header reveal">
            <p className="section-eyebrow">
              <i className="fas fa-fire" /> What's Hot Right Now
            </p>
            <h2 className="section-title">Trending <em>This Season</em></h2>
          </div>
          <div className="row g-4">
            {productsLoading ? (
              <div className="col-12">
                <p className="text-center mb-0">Loading trending products...</p>
              </div>
            ) : productsError ? (
              <div className="col-12">
                <p className="text-center mb-0">{productsError}</p>
              </div>
            ) : trending.length > 0 ? (
              trending.map((p, i) => (
                <div key={p.id} className="col-6 col-md-4 col-lg-3 reveal" style={{ transitionDelay: `${i * 0.07}s` }}>
                  <ProductCard product={p} onAddToCart={handleAddToCart} />
                </div>
              ))
            ) : (
              <div className="col-12">
                <p className="text-center mb-0">No trending products are available right now.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-5 reveal">
            <Link to="/trending" className="btn-outline-navy">
              View All Trending <i className="fas fa-arrow-right trending-arrow-left-margin" />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ══ FEATURE STRIP ═══════════════════════════════════════ */}
      <section className="feature-strip-section">
        <div className="container">
          <div className="row g-4 text-center">
            {[
              { icon: 'fa-gem',          title: 'Certified Authentic',   sub: 'GI Tagged products' },
              { icon: 'fa-truck',        title: 'Free Delivery',         sub: `On orders above ₹${freeShippingAbove.toLocaleString('en-IN')}` },
              { icon: 'fa-rotate-left',  title: '7-Day Returns',         sub: 'Hassle-free policy' },
              { icon: 'fa-headset',      title: 'Expert Support',        sub: '9am–9pm daily' },
            ].map((b, i) => (
              <div key={i} className="col-6 col-md-3 reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="feature-item-pad">
                  <div className="feature-icon-wrap">
                    <i className={`fas ${b.icon}`} />
                  </div>
                  <p className="feature-title">{b.title}</p>
                  <p className="feature-sub">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FEATURED PRODUCTS ═══════════════════════════════════ */}
      {isSectionEnabled('featured') && (
      <section className="section-pad section-white-bg">
        <div className="container">
          <div className="section-header reveal">
            <p className="section-eyebrow">Curated For You</p>
            <h2 className="section-title">Featured <em>Collection</em></h2>
          </div>
          <div className="row g-4">
            {productsLoading ? (
              <div className="col-12">
                <p className="text-center mb-0">Loading featured products...</p>
              </div>
            ) : productsError ? (
              <div className="col-12">
                <p className="text-center mb-0">{productsError}</p>
              </div>
            ) : featured.length > 0 ? (
              featured.map((p, i) => (
                <div key={p.id} className="col-6 col-md-4 col-lg-3 reveal" style={{ transitionDelay: `${i * 0.06}s` }}>
                  <ProductCard product={p} onAddToCart={handleAddToCart} />
                </div>
              ))
            ) : (
              <div className="col-12">
                <p className="text-center mb-0">No featured products are available right now.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-5 reveal">
            <Link to="/shop" className="btn-gold">
              <i className="fas fa-store" /> Shop All Products
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ══ PARALLAX BANNER 2 ═══════════════════════════════════ */}
      <ParallaxSection bgUrl={`${process.env.PUBLIC_URL}/images/cataloguebg-img1.jpg`} overlay="rgba(10,18,36,0.8)">
        <div className="text-center catalogue-wrap">
          <p className="catalogue-eyebrow">
            Exclusively Yours
          </p>
          <h2 className="catalogue-title">
            Download Our <em className="gold-pale-text">Digital Catalogue</em>
          </h2>
          <p className="catalogue-desc">
            Browse 165+ products across Jewellery, Fashion, Hair Care &amp; Home Décor — beautifully laid out, available anytime.
          </p>
          <Link to="/catalogue" className="btn-gold catalogue-btn-style">
            <i className="fas fa-book-open" /> Open Catalogue
          </Link>
        </div>
      </ParallaxSection>

      {/* ══ NEW ARRIVALS ════════════════════════════════════════ */}
      {isSectionEnabled('newarr') && newArrivals.length > 0 && (
        <section className="section-pad section-cream-bg">
          <div className="container">
            <div className="section-header reveal">
              <p className="section-eyebrow">Just Landed</p>
              <h2 className="section-title">New <em>Arrivals</em></h2>
            </div>
            <div className="row g-4">
              {newArrivals.map((p, i) => (
                <div key={p.id} className="col-6 col-md-4 col-lg-3 reveal" style={{ transitionDelay: `${i * 0.07}s` }}>
                  <ProductCard product={p} onAddToCart={handleAddToCart} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ToastContainer toasts={toasts} />
    </>
  );
}